import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, AwaitModules, Button, ComponentSync, Grid, Label, LL_H_C, LL_V_L, openContent } from '@nu-art/thunderstorm-frontend';
import { ProtocolData } from '../../../../_utils/ProtocolData/ProtocolData.js';
import './Component_Protocol.scss';
import { Protocol_DispositionType_Admit, Protocol_DispositionType_Discharge, ProtocolState_Completed, ProtocolStatus_AwaitingDispo, ProtocolStatus_AwaitingInfo, ProtocolStatus_AwaitingTests, ProtocolStatus_Done } from '../../../../_utils/ProtocolData/types.js';
import { _keys, asArray, exists, filterInstances, flatArray, resolveContent } from '@nu-art/ts-common';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_PathwayState, ModuleFE_Advisor_PathwayTransition, ModuleFE_EMInterface, ModuleFE_RuntimeFinding, ModuleFE_RuntimeOrder } from '@app/advisor-frontend';
import { getOrderResourceType } from '../../../../_utils/get-resource-type.js';
import { FindingData } from '../../../../_utils/FindingData/FindingData.js';
import { FindingType_Numeric, FindingType_Textual } from '@app/advisor-shared';
import { ICONS } from '@app/styles';
import { Tooltip_Finding_Numeric } from '@app/advisor-ui-frontend';
import { Popup_Protocol } from '../../next-steps/protocols-overview/Popup_Protocol.js';
import { Component_MakeDecisionProtocol_StepProceed } from '../../next-steps/working-diagnosis/Component_MakeDecision/renderers/Component_MakeDecisionProtocol_StepProceed/Component_MakeDecisionProtocol_StepProceed.js';
import { ModuleFE_ProvideInfo } from '../../../../modules/provide-info/ModuleFE_ProvideInfo.js';
import { contextCloseMode_Safe } from '../../../../modules/provide-info/types.js';
import { pathwayType_SingleState } from '@app/pathway-shared';
import { ModuleFE_AdvisorMini } from '../../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_ProvideInfo } from '@app/advisor-v5-shared';
import { Component_SL_Protocol } from '../../../loaders/skeleton/protocols/Component_AdvisorSL_Protocols.js';
import { getDispositionAction } from '../../../../_utils/disposition-action.js';
import { Renderer_DecisionForkChip } from '../Renderer_DecisionForkChip/Renderer_DecisionForkChip.js';
import { Component_CreateNoteHoverLabel } from '../../create-note-hover/Component_CreateNoteHoverLabel.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../_analytics/consts.js';
import { DDX_HOVER_MS, resolveDdxHoverConsideration } from '../../../../_analytics/resolve-ddx-hover.js';
import { ModuleFE_Notes } from '../../../../modules/ModuleFE_Notes/ModuleFE_Notes.js';
import { getNoteTemplateId } from '../../../../_utils/get-note-template-id.js';
import { ModuleFE_PanelFindings } from '../../../../modules/ModuleFE_PanelFindings/ModuleFE_PanelFindings.js';
class Component extends ComponentSync {
    static modulesToAwait = [
        ...ProtocolData.modulesToAwait,
        ModuleFE_RuntimeFinding,
    ];
    //######################### Life Cycle #########################
    __onRuntimeOrdersUpdated = (...params) => {
        const rtOrders = asArray(params[1]);
        const protocolIds = new Set(flatArray(rtOrders.map(order => _keys(order.protocolMapping ?? {}))));
        if (protocolIds.has(this.state.protocolData.pathway._id))
            this.forceUpdate();
    };
    __onReadOnlyChanged = () => {
        this.forceUpdate();
    };
    hoverTimer;
    deriveStateFromProps(nextProps, state) {
        state.protocolData = nextProps.protocolData;
        state.selected = nextProps.selected;
        state.rankIndex = nextProps.rankIndex;
        return state;
    }
    componentWillUnmount() {
        this.clearHoverTimer();
    }
    clearHoverTimer = () => {
        if (this.hoverTimer === undefined)
            return;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = undefined;
    };
    onProtocolMouseEnter = () => {
        this.clearHoverTimer();
        this.hoverTimer = setTimeout(() => {
            this.hoverTimer = undefined;
            this.reportDdxHover();
        }, DDX_HOVER_MS);
    };
    reportDdxHover = () => {
        const data = this.state.protocolData;
        ModuleFE_AdvisorAnalytics.trigger.ddx.ddx_hover({
            diagnosisId: data.pathway._id,
            diagnosisLabel: data.getLabel(),
            rankIndex: this.state.rankIndex,
            considerationLevel: resolveDdxHoverConsideration(data.getListAssignment(), data.getConsideration()),
        });
    };
    //######################### Logic #########################
    getStyle = () => {
        const style = {};
        const color = this.state.protocolData.getColor();
        if (color)
            style['--p-color'] = color;
        return style;
    };
    resolveResultedPreconditionData = () => {
        const currentActiveNavState = this.state.protocolData.getActiveState();
        const currentDBState = ModuleFE_Advisor_PathwayState.cache.unique(currentActiveNavState?.stateDbId);
        if (!currentDBState)
            return;
        const rtOrders = ModuleFE_RuntimeOrder.getResolvedPreConditionsForPathwayState(currentDBState);
        return filterInstances(rtOrders.map(rtOrder => {
            const dbOrder = ModuleFE_Advisor_Action.cache.unique(rtOrder.orderId);
            if (!dbOrder)
                return;
            const resourceType = getOrderResourceType(dbOrder);
            const findings = ModuleFE_RuntimeFinding.cache.filter(f => f.runtimeOrderId === rtOrder._id && f.type !== FindingType_Textual);
            return {
                rtOrder, dbOrder, resourceType,
                findings: filterInstances(findings.map(f => FindingData.createNew(f))),
            };
        }));
    };
    resolveCurrentStateName = () => {
        const currentActiveNavState = this.state.protocolData.getActiveState();
        const currentDBState = ModuleFE_Advisor_PathwayState.cache.unique(currentActiveNavState?.stateDbId);
        return !!currentDBState ? currentDBState.label ?? currentDBState.name : undefined;
    };
    resolveSubtitleData = () => {
        const data = this.state.protocolData;
        if (data.pathway.type === pathwayType_SingleState)
            return { label: 'Differential only. No protocol' };
        switch (data.getStatus()) {
            case ProtocolStatus_AwaitingTests: {
                const awaitingOrders = data.getAwaitingOrders();
                if (!awaitingOrders?.length)
                    return;
                return { label: `Waiting for ${awaitingOrders[0].label}` };
            }
            case ProtocolStatus_AwaitingInfo: {
                const nextGoToSteps = data.getNextGoToSteps();
                const resolvedPreconditions = this.resolveResultedPreconditionData();
                const currentStateName = this.resolveCurrentStateName();
                //No singular next step, and no resolved preconditions
                if (nextGoToSteps?.length !== 1 && !resolvedPreconditions?.length)
                    return { label: currentStateName };
                //We only have resolved pre-conditions
                if (nextGoToSteps?.length !== 1 && !!resolvedPreconditions?.length) {
                    return { resolvedPreconditions };
                }
                //We only have next go-to steps
                if (nextGoToSteps?.length === 1 && !resolvedPreconditions?.length) {
                    return { label: `${currentStateName} suggests ${nextGoToSteps[0].transition.label}` };
                }
                //We have the whole deal
                if (nextGoToSteps?.length === 1 && !!resolvedPreconditions?.length)
                    return {
                        label: `suggests ${nextGoToSteps[0].transition.label}`,
                        resolvedPreconditions
                    };
                break;
            }
            case ProtocolStatus_Done: {
                return { label: 'Patient reassessment required' };
            }
            case ProtocolState_Completed:
            case ProtocolStatus_AwaitingDispo: {
                const transitionId = data.getActiveState()?.sourceTransitionId;
                const transition = ModuleFE_Advisor_PathwayTransition.cache.unique(transitionId);
                if (transition?.label)
                    return { label: transition.label };
            }
        }
        return;
    };
    getResourceIcon = (resourceType) => {
        switch (resourceType) {
            case 'consult':
                return ICONS.advisor_avn_consultation.component;
            case 'ekg':
                return ICONS.advisor_avn_ekg.component;
            case 'imaging':
                return ICONS.advisor_avn_imaging.component;
            case 'treatment':
                return ICONS.advisor_avn_medication.component;
            default:
                return ICONS.advisor_avn_labs.component;
        }
    };
    getTailData = () => {
        const data = this.state.protocolData;
        if (data.pathway.type === pathwayType_SingleState)
            return { type: ProtocolStatus_AwaitingTests, content: 'Not actionable', className: 'awaiting-tests' };
        switch (data.getStatus()) {
            case ProtocolStatus_AwaitingDispo: {
                const isAdmit = !!data.getAwaitingOrders().find(i => i.resourceType === 'admit');
                if (!isAdmit)
                    return { type: ProtocolStatus_Done, content: 'Completed', className: 'done' };
                const dispoType = data.getAwaitingOrders().find(i => i.resourceType === 'admit') ? 'admit' : 'discharge';
                return {
                    type: ProtocolStatus_AwaitingDispo,
                    content: 'Admit',
                    dispoType,
                    className: 'awaiting-dispo',
                    action: async (e) => {
                        e.stopPropagation();
                        const noteTemplateId = getNoteTemplateId();
                        if (!noteTemplateId)
                            return;
                        const response = await ModuleFE_Notes.generateStampedNote(noteTemplateId, 'organic', data, dispoType);
                        if (response.status === 'fail')
                            return;
                        ModuleFE_Notes.openNotesPanel();
                        ModuleFE_Notes.analytics.note.open();
                    }
                };
            }
            case ProtocolState_Completed: {
                const dispositionType = data.getDispositionType();
                const label = dispositionType === Protocol_DispositionType_Admit
                    ? 'Admitted'
                    : (dispositionType === Protocol_DispositionType_Discharge ? 'Discharged' : 'Completed');
                return { type: ProtocolState_Completed, content: label, className: 'completed' };
            }
            case ProtocolStatus_AwaitingTests:
                return { type: ProtocolStatus_AwaitingTests, content: 'Pending Results', className: 'awaiting-tests' };
            case ProtocolStatus_Done: {
                return { type: ProtocolStatus_Done, content: 'Exited', className: 'done' };
            }
            case ProtocolStatus_AwaitingInfo: {
                const gotoSteps = data.getNextGoToSteps();
                const nextStep = gotoSteps?.length === 1 ? gotoSteps[0] : undefined;
                return { type: ProtocolStatus_AwaitingInfo, content: 'Resolve', nextStep, className: 'awaiting-info' };
            }
        }
    };
    onProtocolClick = (e) => {
        e.stopPropagation();
        const data = this.state.protocolData;
        //Close Context
        if (ModuleFE_ProvideInfo.getCurrentProtocolContextData()?.protocolData.protocol._id === data.protocol._id) {
            ModuleFE_ProvideInfo.context.chain.close(contextCloseMode_Safe);
        }
        else { //Open Context
            if (data.getStatus() !== ProtocolStatus_AwaitingInfo && data.pathway.type !== pathwayType_SingleState)
                return;
            ModuleFE_ProvideInfo.protocol.open(data.protocol._id, data.getStatus() === ProtocolStatus_AwaitingInfo);
            if (ModuleFE_AdvisorMini.getIsMiniActive())
                ModuleFE_AdvisorMini.openPanel(PanelKey_ProvideInfo);
        }
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_H_C, { className: 'c__protocol', style: this.getStyle(), onClick: this.onProtocolClick, onMouseEnter: this.onProtocolMouseEnter, onMouseLeave: this.clearHoverTimer, onContextMenu: e => Popup_Protocol.show(e, this.state.protocolData), children: [this.render_Head(), this.render_Tail()] });
    }
    render_Head = () => {
        return _jsxs(LL_V_L, { className: 'c__protocol-head', children: [this.render_Head_Title(), this.render_Head_Subtitle()] });
    };
    render_Head_Title = () => {
        const label = this.state.protocolData.getLabel();
        return _jsx(Label, { className: 'title', tooltip: label, children: label });
    };
    render_Head_Subtitle = () => {
        const subtitleData = this.resolveSubtitleData();
        if (!subtitleData)
            return;
        return _jsxs(LL_H_C, { className: 'c__protocol-subtitle', children: [this.render_Head_Subtitle_Precondition(subtitleData?.resolvedPreconditions), exists(subtitleData?.label) && _jsx(Label, { tooltip: subtitleData.label, children: subtitleData.label })] });
    };
    render_Head_Subtitle_Precondition = (preconditions) => {
        if (!preconditions?.length)
            return;
        //Only singular numeric value for the first precondition
        if (preconditions[0].findings.length === 1 && preconditions[0].findings[0].finding.type === FindingType_Numeric) {
            const finding = preconditions[0].findings[0];
            const value = finding.getValue(true);
            const className = _className('numeric-finding', finding.isAbnormal() && 'abnormal');
            return _jsx(Tooltip_Finding_Numeric, { modules: FindingData.tooltipModules, data: finding.getTooltipData(), children: _jsx("div", { className: className, onClick: (e) => {
                        e.stopPropagation();
                        ModuleFE_PanelFindings.jumpToFinding(preconditions[0].rtOrder._id);
                    }, children: value }) });
        }
        //Not a singular numeric value, render under an icon
        const Icon = this.getResourceIcon(preconditions[0].resourceType);
        const hasAbnormalResults = preconditions[0].findings.some(f => f.isAbnormal());
        const className = _className('multi-findings', hasAbnormalResults && 'abnormal');
        return _jsx(Icon, { className: className, ...openContent.tooltip.right('tt__protocol-precondition-results', () => this.render_TT_PreconditionResults(preconditions[0])), onClick: (e) => {
                e.stopPropagation();
                ModuleFE_PanelFindings.jumpToFinding(preconditions[0].rtOrder._id);
            } });
    };
    render_Tail = () => {
        const data = this.getTailData();
        if (!data)
            return;
        switch (data.type) {
            case ProtocolStatus_AwaitingDispo: {
                const disabled = ModuleFE_EMInterface.isReadonly();
                const { label, variant, Icon } = getDispositionAction(data.dispoType);
                return _jsxs(Button, { variant: variant, onClick: data.action, className: _className('c__protocol-tail', data.className), disabled: disabled, children: [_jsx(Icon.component, {}), _jsx(Component_CreateNoteHoverLabel, { resting: label })] });
            }
            case ProtocolStatus_AwaitingInfo:
                if (exists(data.nextStep))
                    return _jsx(Component_MakeDecisionProtocol_StepProceed, { nextStep: data.nextStep, outerContext: this.state.protocolData });
                return _jsx(Renderer_DecisionForkChip, {});
            default:
                return _jsx("div", { className: _className('c__protocol-tail', data.className), children: resolveContent(data.content) });
        }
    };
    // ######################## Render - TT Content ########################
    render_TT_PreconditionResults = (data) => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'title', children: data.dbOrder.label ?? data.dbOrder.name }), _jsx(Grid, { className: 'finding-grid', children: data.findings.map(f => {
                        const className = _className('value', f.isAbnormal() && 'abnormal');
                        return _jsxs(Grid, { children: [_jsx("div", { className: 'label', children: f.getLabel() }), _jsx("div", { className: className, children: f.getValue(true) })] }, f.finding._id);
                    }) })] });
    };
}
export const Component_Protocol = (p) => _jsx(AwaitModules, { modules: Component.modulesToAwait, Component: () => _jsx(Component, { ...p }), customLoader: () => _jsx(Component_SL_Protocol, {}) });
//# sourceMappingURL=Component_Protocol.js.map